import { useApiQuery } from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useRoomMembership } from "./useIsRoomMember";

/**
 * Whether the signed-in reader may load the room's private row: the rule
 * `guardRoomListing` applies to `GET /webapp/private/rooms/:id` (a member, or a
 * public or discoverable room). `pending` while the answer loads, so a caller
 * that redirects can wait for it instead of acting on a guess.
 *
 * A shared resource page asks this before it asks for the row. The reader a
 * shared link brings is often a non-member of a private client space, the row
 * answers them 403, and the api client reports every 403 to Sentry as an error.
 * The flags come from the room's PUBLIC card, which answers everyone.
 *
 * Staff pass `guardRoomListing` too; the client does not know the roles, so a
 * staff member outside the room reads the page without the room's dressing.
 */
export function useCanOpenRoom(
  roomId: string | null
): "pending" | "open" | "closed" {
  const { isAuthenticated, isLoading } = useCurrentUser();
  const membership = useRoomMembership(roomId);
  const cardQuery = useApiQuery(
    "/webapp/public/rooms/:roomId",
    roomId !== null ? { param: { roomId }, query: {} } : false
  );
  if (isLoading) return "pending";
  if (!isAuthenticated || roomId === null) return "closed";
  if (membership === "member") return "open";
  if (cardQuery.state === "success") {
    const { isPublic, discoverable } = cardQuery.body;
    if (isPublic || discoverable) return "open";
  }
  if (membership === "pending" || cardQuery.state === "pending") {
    return "pending";
  }
  return "closed";
}
